/**
 * Collect answered questionnaire fields with human-readable labels and item paths.
 * Paths match TemplatePreviewDrawer `data-item-path` for scroll-to.
 */
import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import { isQuestionEnabled, QuestionnaireItemType } from '@medplum/react-hooks';
import { PARTICIPANT_ATTENDANCE_GROUP_LINKID } from '@/pages/appointments/utils/participantAttendance';
import { buildQuestionnaireItemPath } from './questionnaireResponseRequired';

export interface QuestionnaireAnswerField {
  itemPath: string;
  label: string;
  text: string;
}

function stripHtml(s: string): string {
  return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function textFromResponseItem(ri: QuestionnaireResponseItem): string {
  const parts: string[] = [];
  for (const answer of ri.answer ?? []) {
    if (answer.valueString?.trim()) {
      parts.push(stripHtml(answer.valueString));
    }
  }
  return parts.join('\n').trim();
}

function collectAnswerFields(
  qItems: QuestionnaireItem[],
  responseItems: QuestionnaireResponseItem[] | undefined,
  qr: QuestionnaireResponse,
  parentPath: string,
): QuestionnaireAnswerField[] {
  const fields: QuestionnaireAnswerField[] = [];
  const rItems = responseItems ?? [];

  for (const item of qItems) {
    if (!isQuestionEnabled(item, qr)) continue;
    if (item.linkId === PARTICIPANT_ATTENDANCE_GROUP_LINKID) continue;

    const label = (item.text ?? item.linkId ?? 'Question').trim() || item.linkId || 'Question';
    const itemPath = buildQuestionnaireItemPath(item.linkId, parentPath);

    if (item.type === QuestionnaireItemType.display) continue;

    if (item.type === QuestionnaireItemType.group) {
      const filtered = rItems.filter((ri) => ri.linkId === item.linkId);
      const childParent = buildQuestionnaireItemPath(item.linkId, parentPath);
      if (item.repeats) {
        for (const ri of filtered) {
          fields.push(...collectAnswerFields(item.item ?? [], ri.item ?? [], qr, childParent));
        }
      } else {
        fields.push(...collectAnswerFields(item.item ?? [], filtered[0]?.item ?? [], qr, childParent));
      }
      continue;
    }

    const filtered = rItems.filter((ri) => ri.linkId === item.linkId);
    const ri = filtered[0];
    if (!ri) continue;

    const text = textFromResponseItem(ri);
    if (text) {
      fields.push({ itemPath, label, text });
    }
  }

  return fields;
}

export function collectQuestionnaireAnswerFields(
  questionnaire: Questionnaire,
  response: QuestionnaireResponse,
): QuestionnaireAnswerField[] {
  if (!questionnaire.item?.length) return [];
  return collectAnswerFields(questionnaire.item, response.item, response, '');
}
